import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Opcion, Pagina, TipoFactura } from '../api/tipos';
import { Cargando, Error } from '../componentes/comunes';
import { useSesion } from '../sesion';

/**
 * Reporte de lo facturado.
 *
 * El corte es por fecha de expedición y no por cierre de la orden: lo que
 * se reporta es lo que se cobró, y una orden cerrada en julio y facturada
 * en agosto es facturación de agosto.
 *
 * La columna que hace que esto sirva para algo más que informar es la de
 * sin timbrar. Una prefactura sin UUID es dinero que el cliente no puede
 * pagar todavía, aunque el trabajo ya esté hecho.
 *
 * Los números salen de la API, no de sumar aquí lo que llegó: el detalle
 * viene acotado a cien renglones y sumarlo daría un total más chico que el
 * verdadero, que es la peor clase de error porque parece correcto.
 */

interface Totales {
  cuantas: number;
  subtotal: number;
  iva: number;
  total: number;
  sinTimbrar: number;
  importeSinTimbrar: number;
}

interface Grupo extends Totales {
  clave: string;
  etiqueta: string;
}

interface Renglon {
  folio: string;
  tipo: string;
  receptor: string;
  sucursal: string | null;
  poNumero: string;
  fechaExpedicion: string;
  total: number;
  timbrada: boolean;
  cancelada: boolean;
}

interface Reporte {
  totales: Totales;
  porCliente: Grupo[];
  porTipo: Grupo[];
  porMes: Grupo[];
  detalle: Renglon[];
  detalleCompleto: boolean;
  /**
   * Las canceladas del periodo, que no entran en nada de lo anterior.
   *
   * Se enseñan aunque no sumen. Un reporte que omite en silencio deja un
   * hueco en la serie de folios y a nadie con qué explicarlo.
   */
  canceladas: number;
  importeCancelado: number;
}

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/** El primer día del mes en curso, que es el corte que se pide siempre. */
function inicioDeMes(): string {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
}

const NOMBRE_TIPO: Record<string, string> = {
  iguala: 'Iguala',
  correctivo: 'Correctivo',
  libre: 'Libre',
};

export default function ReporteFacturacion() {
  const { puede } = useSesion();
  const [desde, setDesde] = useState(inicioDeMes());
  const [hasta, setHasta] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [tipo, setTipo] = useState<'' | TipoFactura>('');
  const [sinTimbrar, setSinTimbrar] = useState('');
  const [clientes, setClientes] = useState<Opcion[]>([]);
  const [reporte, setReporte] = useState<Reporte | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<Pagina<Opcion>>('/clientes', { limite: 200, estado: 'activo' })
      .then((r) => setClientes(r.data))
      .catch(() => undefined);
  }, []);

  const parametros = useCallback(() => ({
    desde: desde || undefined,
    hasta: hasta || undefined,
    clienteId: clienteId || undefined,
    tipo: tipo || undefined,
    sinTimbrar: sinTimbrar || undefined,
  }), [desde, hasta, clienteId, tipo, sinTimbrar]);

  useEffect(() => {
    setCargando(true);
    setError(null);
    api.get<Reporte>('/facturacion/reporte', parametros())
      .then(setReporte)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [parametros]);

  return (
    <>
      <div className="panel">
        <div className="rejilla-campos">
          <div className="campo">
            <label htmlFor="desde">Desde</label>
            <input id="desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="hasta">Hasta</label>
            <input id="hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
            <span className="ayuda">Vacío llega hasta hoy. Los dos extremos cuentan.</span>
          </div>
          <div className="campo">
            <label htmlFor="cliente">Cliente</label>
            <select id="cliente" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">Todos</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="tipo">Tipo</label>
            <select
              id="tipo" value={tipo}
              onChange={(e) => setTipo(e.target.value as '' | TipoFactura)}
            >
              <option value="">Todos</option>
              <option value="iguala">Iguala</option>
              <option value="correctivo">Correctivo</option>
              <option value="libre">Libre</option>
            </select>
          </div>
          <div className="campo">
            <label htmlFor="timbrado">Timbrado</label>
            <select
              id="timbrado" value={sinTimbrar}
              onChange={(e) => setSinTimbrar(e.target.value)}
            >
              <option value="">Todo</option>
              <option value="si">Sólo sin timbrar</option>
              <option value="no">Sólo timbradas</option>
            </select>
          </div>
        </div>

        {/* Contabilidad también. La API acepta el permiso de cualquiera
            de las dos áreas —«contabilidad necesita lo facturado para
            cuadrar el mes»— y aquí sólo se miraba el de facturación, así
            que quien podía descargarlo no veía el botón. */}
        {(puede('facturacion', 'exportar') || puede('contabilidad', 'exportar')) && (
          <div className="controles" style={{ marginTop: 12 }}>
            <button
              onClick={() => {
                void api.descargar('/facturacion/reporte/exportar', parametros());
              }}
            >
              Descargar Excel
            </button>
            <span className="conteo">
              El archivo lleva todos los renglones y los filtros escritos dentro.
            </span>
          </div>
        )}
      </div>

      <Error error={error} />

      {cargando && !reporte ? <Cargando /> : reporte && (
        <>
          <div className="rejilla-catalogos">
            <Tarjeta titulo="Facturas" valor={String(reporte.totales.cuantas)} />
            <Tarjeta titulo="Subtotal" valor={moneda(reporte.totales.subtotal)} />
            <Tarjeta titulo="IVA" valor={moneda(reporte.totales.iva)} />
            <Tarjeta titulo="Total" valor={moneda(reporte.totales.total)} />
            <Tarjeta
              titulo="Sin timbrar"
              valor={moneda(reporte.totales.importeSinTimbrar)}
              nota={`${reporte.totales.sinTimbrar} prefacturas`}
              alerta={reporte.totales.sinTimbrar > 0}
            />
          </div>

          {/* Lo que el reporte dejó fuera, dicho donde se ven los totales.
              No es un filtro que alguien eligió: es una omisión que el
              reporte hace solo, y sin esta línea la primera pregunta al
              recibir el archivo es por qué falta un folio de la serie. */}
          {reporte.canceladas > 0 && (
            <p className="sub">
              {`Fuera del reporte: ${reporte.canceladas === 1
                ? '1 factura cancelada'
                : `${reporte.canceladas} facturas canceladas`} `}
              por {moneda(reporte.importeCancelado)}. No suman aquí ni salen en el Excel.
            </p>
          )}

          {reporte.totales.cuantas === 0 ? (
            <div className="panel">
              <div className="vacio">
                {reporte.canceladas > 0
                  ? 'Todo lo de ese periodo está cancelado.'
                  : 'No hay nada facturado en ese periodo.'}
              </div>
            </div>
          ) : (
            <>
              <div className="panel">
                <h2>Por mes</h2>
                <p className="sub">
                  En orden de calendario, para que se lea la tendencia.
                </p>
                <TablaGrupos grupos={reporte.porMes} columna="Mes" />
              </div>

              <div className="panel">
                <h2>Por cliente</h2>
                <TablaGrupos grupos={reporte.porCliente} columna="Cliente" />
              </div>

              {/* El bloque «Por tipo» se retiró a pedido de Karin.

                  Agrupar por iguala, correctivo y libre decía poco: la
                  iguala se lleva casi todo el importe por su naturaleza,
                  así que el corte salía siempre igual y no contestaba
                  ninguna pregunta que alguien se estuviera haciendo. El
                  tipo sigue como filtro arriba y como columna en el
                  detalle, que es donde sí sirve — para acotar, no para
                  resumir.

                  La API sigue devolviendo `porTipo` y hoy no lo consume
                  nadie —el Excel lleva el detalle, no los agrupados—. Se
                  deja porque es un group-by y su función pura conserva
                  sus pruebas; si en un mes nadie lo pide, se quita de la
                  respuesta y se acabó. */}

              <div className="panel">
                <h2>Detalle</h2>
                {!reporte.detalleCompleto && (
                  <p className="sub">
                    Se muestran los primeros 100. El Excel los lleva todos.
                  </p>
                )}
                <table>
                  <thead>
                    <tr>
                      <th>Folio</th>
                      <th>Tipo</th>
                      <th>Fecha</th>
                      <th>Receptor</th>
                      <th>Sucursal</th>
                      <th className="numero">Total</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reporte.detalle.map((f) => (
                      <tr key={f.folio}>
                        <td className="mono">{f.folio}</td>
                        <td>{NOMBRE_TIPO[f.tipo] ?? f.tipo}</td>
                        <td>{fechaCorta(f.fechaExpedicion)}</td>
                        <td>{f.receptor}</td>
                        <td>{f.sucursal ?? '—'}</td>
                        <td className="numero">{moneda(f.total)}</td>
                        <td>
                          {f.cancelada
                            ? <span className="conteo">Cancelada</span>
                            : f.timbrada
                              ? <span className="etiqueta si">Timbrada</span>
                              : <span className="etiqueta no">Prefactura</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

function TablaGrupos({ grupos, columna }: { grupos: Grupo[]; columna: string }) {
  return (
    <table>
      <thead>
        <tr>
          <th>{columna}</th>
          <th className="numero">Facturas</th>
          <th className="numero">Subtotal</th>
          <th className="numero">Total</th>
          <th className="numero">Sin timbrar</th>
        </tr>
      </thead>
      <tbody>
        {grupos.map((g) => (
          <tr key={g.clave}>
            <td>{g.etiqueta}</td>
            <td className="numero">{g.cuantas}</td>
            <td className="numero">{moneda(g.subtotal)}</td>
            <td className="numero">{moneda(g.total)}</td>
            <td className="numero">
              {g.sinTimbrar > 0
                ? <span className="etiqueta no">{moneda(g.importeSinTimbrar)}</span>
                : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Tarjeta({
  titulo, valor, nota, alerta,
}: {
  titulo: string; valor: string; nota?: string; alerta?: boolean;
}) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
      {nota && <div className="conteo">{nota}</div>}
    </div>
  );
}
